// Three months side by side — wide planning, in Zanetto Tools' shape.
//
// Three separate month cards from `lg:` (stacked below), each 48px-tall
// cells with per-job 6px COLOUR BARS and no text. The bars are a recorded
// accessibility exception (round 22): below the app's touch-target floor,
// kept because ZT's 2026-08-14 button audit found enlarging them cost the
// view its whole point — density. Title and aria-label carry the content,
// and the day panel one tap away holds the full answer.
//
// The grey overflow bar is CLICKABLE here — a Dayboard extra; ZT's is inert.
import { useMemo } from 'react';
import { monthLabel, sameMonth, todayISO, WEEKDAY_LABELS, type DateOnly } from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { groupDayByJob, type JobDayGroup } from '../../lib/jobGroups';
import { nwShade, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import type { CalendarBooking } from '../../schedulingApi';

const BAR_LIMIT = 3;

function chunkWeeks(days: DateOnly[]): DateOnly[][] {
  const weeks: DateOnly[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export function ThreeMonthView({
  months,
  byDay,
  onOpenDay,
  onSelectGroup,
  nwInfo,
  worksOn,
}: {
  months: { anchor: DateOnly; days: DateOnly[] }[];
  byDay: Map<string, CalendarBooking[]>;
  onOpenDay: (date: DateOnly) => void;
  onSelectGroup: (group: JobDayGroup, date: DateOnly) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
}) {
  const today = todayISO();

  const groupsByDay = useMemo(() => {
    const map = new Map<DateOnly, JobDayGroup[]>();
    for (const month of months) {
      for (const date of month.days) {
        if (map.has(date)) continue;
        const dayBookings = (byDay.get(date) ?? []).filter((b) => worksOn(b, date));
        map.set(date, groupDayByJob(dayBookings));
      }
    }
    return map;
  }, [months, byDay, worksOn]);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {months.map((month) => (
        <section key={month.anchor} className="card overflow-hidden">
          <header className="border-b border-gray-200 bg-gray-50 px-3 py-2.5">
            <h3 className="text-sm font-semibold text-gray-800">{monthLabel(month.anchor)}</h3>
          </header>

          <div className="grid grid-cols-7 border-b border-gray-100">
            {WEEKDAY_LABELS.map((label, index) => (
              <div
                key={index}
                className="border-r border-gray-100 py-1 text-center text-xs font-medium text-gray-400 last:border-r-0"
              >
                {label[0]}
              </div>
            ))}
          </div>

          {chunkWeeks(month.days).map((week) => (
            <div key={week[0]} className="grid grid-cols-7">
              {week.map((date) => {
                const nw = nwInfo(date);
                const isToday = date === today;
                const inMonth = sameMonth(date, month.anchor);
                const groups = groupsByDay.get(date) ?? [];
                const overflow = groups.length - BAR_LIMIT;

                const dateColour = isToday
                  ? 'font-bold text-brand-800'
                  : nw?.dated
                    ? 'font-medium text-red-700'
                    : inMonth
                      ? 'text-gray-600'
                      : 'text-gray-300';

                return (
                  <div
                    key={date}
                    className={`min-h-12 border-r border-gray-100 p-0.5 last:border-r-0 ${
                      isToday ? 'bg-brand-50' : nwShade(nw)
                    }`}
                  >
                    <div className={`mb-0.5 text-right text-xs leading-tight ${dateColour}`}>
                      {Number(date.slice(8, 10))}
                    </div>
                    {groups.slice(0, BAR_LIMIT).map((group) => {
                      const colour = jobColour(group.jobNumber, group.jobId, group.inConflict);
                      return (
                        <button
                          key={group.key}
                          type="button"
                          onClick={() => onSelectGroup(group, date)}
                          title={`${group.subtitle ?? ''} · ${group.title} · ${group.resources.join(', ')}`}
                          aria-label={`${group.subtitle ?? group.title} on ${date}`}
                          className={`mb-0.5 block h-1.5 w-full rounded-sm transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.bar}`}
                        />
                      );
                    })}
                    {overflow > 0 && (
                      <button
                        type="button"
                        onClick={() => onOpenDay(date)}
                        title={`+${overflow} more`}
                        aria-label={`${overflow} more on ${date}`}
                        className="mb-0.5 block h-1.5 w-full rounded-sm bg-gray-300 transition-opacity hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-brand-600"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
